import * as ww from '@wecom/jssdk'
import wecomApi from '@/api/modules/wecom'
/**
 * 企业微信工具类
 */

// 配置状态
let isRegistered = false
let isConfigReady = false
let corpId = ''
let agentId = ''

/**
 * 检查是否在企业微信环境中
 */
export function isWecomEnvironment(): boolean {
  const ua = navigator.userAgent
  return ua.includes('wxwork') || ua.includes('MicroMessenger')
}

/**
 * 注册企业微信 JS-SDK
 */
export async function registerWecomJsSDK(): Promise<boolean> {
  if (isRegistered) {
    return isConfigReady
  }

  try {
    const response = await wecomApi.getConfig()
    const initial = response.data

    if (!initial || !initial.appid) {
      console.warn('企业微信未配置或配置无效')
      return false
    }

    corpId = initial.appid
    agentId = initial.agentid || ''

    // 注册 SDK：提供获取签名的方法（按 URL 动态获取）
    ww.register({
      corpId: initial.appid,
      agentId: initial.agentid,
      jsApiList: [
        'getContext',
        'login',
        'selectEnterpriseContact',
        'selectExternalContact',
        'openEnterpriseChat',
        'openUserProfile',
        'openExistedChatWithMsg',
        'selectPrivilegedContact',
        'createChatWithMsg',
        'createCorpGroupChat',
      ],
      async getConfigSignature(url: string) {
        const resp = await wecomApi.getConfigSignature(url)
        const cfg = resp.data
        return {
          timestamp: cfg.timestamp,
          nonceStr: cfg.nonceStr,
          signature: cfg.signature,
        }
      },
      async getAgentConfigSignature(url: string) {
        const resp = await wecomApi.getConfigSignature(url)
        const cfg = resp.data
        return {
          timestamp: cfg.timestamp,
          nonceStr: cfg.nonceStr,
          signature: cfg.agentSignature || cfg.signature,
        }
      },
      onConfigSuccess() {
        isConfigReady = true
      },
      onConfigFail(err: any) {
        console.error('企业微信 JS-SDK 配置失败:', err)
        isConfigReady = false
      },
    })

    await ww.ensureConfigReady()
    isRegistered = true
    isConfigReady = true
    return true
  }
  catch (err: any) {
    console.error('注册企业微信 JS-SDK 失败:', err)
    isRegistered = false
    isConfigReady = false
    return false
  }
}

/**
 * 打开成员资料页（用于联系群主/管理员）
 */
export async function openWecomUserProfile(params: { userid: string, type?: 1 | 2 }): Promise<void> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }
  const userid = String(params.userid || '').trim()
  if (!userid) {
    throw new Error('userid不能为空')
  }

  await ww.openUserProfile({
    type: params.type || 1,
    userid,
  })
}

/**
 * 选择企业微信外部联系人（客户）
 */
export async function selectWecomCustomers(): Promise<string[]> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const res: any = await ww.selectExternalContact({
    filterType: 0,
  })

  const ids = (res?.userIds || []) as string[]
  return Array.isArray(ids) ? ids : []
}

/**
 * 选择企业内部成员
 */
export interface WecomSelectedInternalUser {
  id: string
  name?: string
  avatar?: string
}

export async function selectWecomInternalUsersDetailed(params?: {
  selectedUserIds?: string[]
  mode?: 'single' | 'multi'
}): Promise<WecomSelectedInternalUser[]> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const res: any = await ww.selectEnterpriseContact({
    fromDepartmentId: 0,
    mode: params?.mode === 'single' ? ww.SelectEnterpriseContactMode.single : ww.SelectEnterpriseContactMode.multi,
    type: [ww.SelectEnterpriseContactType.user],
    selectedUserIds: params?.selectedUserIds || [],
  })

  const users = (res?.result?.userList || [])
    .map((u: any) => ({
      id: String(u?.id || '').trim(),
      name: String(u?.name || '').trim() || undefined,
      avatar: String(u?.avatar || '').trim() || undefined,
    }))
    .filter((u: any) => Boolean(u.id)) as WecomSelectedInternalUser[]

  return Array.isArray(users) ? users : []
}

export async function selectWecomInternalUsers(params?: {
  selectedUserIds?: string[]
  mode?: 'single' | 'multi'
}): Promise<string[]> {
  const users = await selectWecomInternalUsersDetailed(params)
  return users.map(u => u.id)
}

/**
 * 打开/创建客户群聊
 * - 传 chatId：打开已有客户群
 * - 传 externalUserIds：创建会话
 */
export async function openWecomCustomerChat(params: {
  chatId?: string
  groupName?: string
  externalUserIds?: string[]
  userIds?: string[]
}): Promise<{ chatId?: string }> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }
  if (import.meta.env.DEV) {
    console.warn('openWecomCustomerChat params:', params)
  }
  const res: any = await ww.openEnterpriseChat({
    chatId: params.chatId,
    groupName: params.groupName,
    externalUserIds: params.externalUserIds,
    userIds: params.userIds,
  })

  return { chatId: res?.chatId }
}

/**
 * 创建企业互联/上下游会话（可包含外部联系人）
 * 文档：https://developer.work.weixin.qq.com/document/path/96936
 */
export async function createWecomCorpGroupChat(params: {
  groupName?: string
  externalUserIds?: string[]
  userIds?: string[]
  openUserIds?: string[]
  corpGroupUserIds?: Array<{
    corpId: string
    userId?: string
    openUserId?: string
  }>
}): Promise<{ chatId: string }> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  const res: any = await ww.createCorpGroupChat({
    groupName: params.groupName,
    externalUserIds: params.externalUserIds,
    userIds: params.userIds,
    openUserIds: params.openUserIds,
    corpGroupUserIds: params.corpGroupUserIds,
  })

  const chatId = String(res?.chatId || '')
  return { chatId }
}

/**
 * 获取企业微信配置状态
 */
export function getWecomConfig() {
  return {
    isRegistered,
    isConfigReady,
    corpId,
    agentId,
  }
}

/**
 * 调用企业微信 JS-SDK 的 login 方法
 */
export async function invokeWecomLogin(): Promise<string> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  return new Promise((resolve, reject) => {
    ww.invoke('login', {})
      .then((res: any) => {
        const ok = res?.errMsg === 'login:ok' || res?.err_msg === 'login:ok'
        if (ok && res?.code) {
          resolve(res.code)
        }
        else {
          reject(new Error(res?.errMsg || res?.err_msg || '登录失败'))
        }
      })
      .catch((err: any) => {
        reject(err)
      })
  })
}

/**
 * 创建企业微信登录面板
 */
export function createWecomLoginPanel(
  el: HTMLElement,
  params: any,
  callbacks: {
    onLoginSuccess?: (res: any) => void
    onLoginFail?: (err: any) => void
    onCheckWeComLogin?: () => void
  },
) {
  return ww.createWWLoginPanel({
    el,
    params,
    onLoginSuccess: callbacks.onLoginSuccess,
    onLoginFail: callbacks.onLoginFail,
    onCheckWeComLogin: callbacks.onCheckWeComLogin,
  })
}

/**
 * 获取当前上下文信息
 */
export async function getWecomContext(): Promise<any> {
  if (!isConfigReady) {
    throw new Error('企业微信 JS-SDK 未准备就绪')
  }

  return ww.invoke('getContext', {})
}

/**
 * 重置配置状态（用于测试或重新初始化）
 */
export function resetWecomConfig() {
  isRegistered = false
  isConfigReady = false
  corpId = ''
  agentId = ''
}

/**
 * 从 URL 中获取企业微信授权 code 和 state
 */
export function getWecomCodeFromUrl(): { code: string | null, state: string | null } {
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const state = urlParams.get('state')

  return { code, state }
}

/**
 * 检查 URL 中是否有企业微信授权 code
 */
export function hasWecomCode(): boolean {
  const { code, state } = getWecomCodeFromUrl()
  return !!(code && state)
}

/**
 * 清除 URL 中的企业微信授权相关参数
 */
export function cleanWecomUrlParams() {
  const url = new URL(window.location.href)
  const params = url.searchParams

  // 移除授权相关参数
  params.delete('code')
  params.delete('state')

  // 构建新的 URL（保留 hash）
  const newUrl = `${url.origin}${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash}`

  // 使用 replaceState 替换 URL，不会触发页面刷新
  window.history.replaceState({}, document.title, newUrl)
}

/**
 * 构建 OAuth2 授权 URL
 */
export async function buildOAuth2Url(
  getParamsFn: (state: string) => Promise<any>,
  redirectUri?: string,
): Promise<string> {
  const state = `wecom_login_${Date.now()}`
  const response = await getParamsFn(state)
  const params = response.data || {}

  const authUrl = new URL('https://open.weixin.qq.com/connect/oauth2/authorize')
  authUrl.searchParams.set('appid', params.appid)
  authUrl.searchParams.set('redirect_uri', encodeURIComponent(redirectUri || params.redirect_uri || window.location.href))
  authUrl.searchParams.set('response_type', 'code')
  authUrl.searchParams.set('scope', params.scope || 'snsapi_base')
  authUrl.searchParams.set('state', state)
  authUrl.hash = 'wechat_redirect'

  return authUrl.toString()
}

/**
 * 跳转到 OAuth2 授权页面
 */
export async function redirectToOAuth2(
  getParamsFn: (state: string) => Promise<any>,
  redirectUri?: string,
) {
  const url = await buildOAuth2Url(getParamsFn, redirectUri)
  window.location.href = url
}

/**
 * 检查 URL 参数中是否有绑定标记
 */
export function hasWecomBindFlag(): boolean {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('wecom_bind') === '1'
}

/**
 * 添加绑定标记到 URL
 */
export function addWecomBindFlag(url: string): string {
  const urlObj = new URL(url)
  urlObj.searchParams.set('wecom_bind', '1')
  return urlObj.toString()
}

/**
 * 使用 iframe 发起 OAuth2 授权
 * @param getParamsFn 获取授权参数的函数
 * @param redirectUri 回调 URL
 * @returns Promise<{code: string, state: string}> 返回授权码和状态
 */
export async function authorizeWithIframe(
  getParamsFn: (state: string) => Promise<any>,
  redirectUri?: string,
): Promise<{ code: string, state: string }> {
  let iframe: HTMLIFrameElement | null = null
  let checkTimer: NodeJS.Timeout | null = null
  let timeoutTimer: NodeJS.Timeout | null = null

  // 清理函数
  const cleanup = () => {
    if (checkTimer) {
      clearInterval(checkTimer)
      checkTimer = null
    }
    if (timeoutTimer) {
      clearTimeout(timeoutTimer)
      timeoutTimer = null
    }
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe)
      iframe = null
    }
  }

  try {
    // 构建 OAuth2 授权 URL
    const authUrl = await buildOAuth2Url(getParamsFn, redirectUri)

    return new Promise((resolve, reject) => {
      // 创建隐藏的 iframe
      iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = authUrl
      document.body.appendChild(iframe)

      // 定时检查 iframe 的 URL
      checkTimer = setInterval(() => {
        try {
          if (!iframe || !iframe.contentWindow) {
            return
          }

          // 尝试访问 iframe 的 location
          const iframeLocation = iframe.contentWindow.location.href

          // 检查是否包含 code 参数
          const url = new URL(iframeLocation)
          const code = url.searchParams.get('code')
          const returnedState = url.searchParams.get('state')

          if (code && returnedState) {
            cleanup()
            resolve({ code, state: returnedState })
          }
        }
        catch {
          // 跨域错误是正常的，忽略
          // 只有当 iframe 跳转回同域的回调 URL 时才能访问
        }
      }, 100)

      // 设置超时（30秒）
      timeoutTimer = setTimeout(() => {
        cleanup()
        reject(new Error('授权超时'))
      }, 30000)
    })
  }
  catch (err) {
    cleanup()
    throw err
  }
}

/**
 * 处理企业微信授权回调自动登录
 * @param loginFn 登录函数，接收 code 和 state，返回登录响应
 * @returns 登录结果，如果没有 code 则返回 null
 */
export async function handleWecomAutoLogin(
  loginFn: (code: string, state: string) => Promise<any>,
): Promise<any | null> {
  const { code, state } = getWecomCodeFromUrl()

  if (!code || !state) {
    return null
  }

  try {
    // 调用登录函数
    const result = await loginFn(code, state)

    // 清除 URL 中的 code 和 state 参数
    cleanWecomUrlParams()

    return result
  }
  catch (err) {
    // 登录失败，清除 URL 参数
    cleanWecomUrlParams()
    throw err
  }
}
